/**
 * The app roles rollout switch (ADR-034), on the Dev Console's Arche
 * Management page beside the ADR-026 governance switches.
 *
 *   off    no app may publish its FIRST roles policy
 *   pilot  only the listed apps may
 *   on     any app may
 *
 * CREATION-ONLY, like the governance switches: an app that already has roles
 * keeps them, keeps being enforced, and keeps publishing changes to them
 * whatever this says — turning it off must never open data that roles
 * protect. Admitting more asks first; admitting less is one click. The
 * server reads off when the setting is missing or unreadable.
 *
 * Server state lives in React Query under
 * `queryKeys.adminArcheGovernance.appRolesRollout()`; the mutation writes the
 * server's answer back into that cache.
 */

import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import api, { type AppRolesRolloutDto, type AppRolesRolloutMode } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const ARCHE_ID = /^arc_[a-f0-9]{32}$/;
const PILOT_MAX = 50;

const MODES: Array<{ mode: AppRolesRolloutMode; label: string; meaning: string }> = [
  {
    mode: 'off',
    label: 'Off',
    meaning:
      'No app can publish roles for the first time. Apps that already have roles keep them and keep being enforced.',
  },
  {
    mode: 'pilot',
    label: 'Pilot',
    meaning: 'Only the apps listed below can publish roles for the first time.',
  },
  { mode: 'on', label: 'On', meaning: 'Any app can publish roles.' },
];

const RANK: Record<AppRolesRolloutMode, number> = { off: 0, pilot: 1, on: 2 };

/** Does `next` let an app publish roles that `current` would not? */
function admitsMore(current: AppRolesRolloutDto, next: AppRolesRolloutDto): boolean {
  if (RANK[next.mode] !== RANK[current.mode]) return RANK[next.mode] > RANK[current.mode];
  if (next.mode !== 'pilot') return false;
  return next.pilotArcheIds.some((id) => !current.pilotArcheIds.includes(id));
}

function parsePilot(text: string): { ids: string[]; invalid: string[] } {
  const tokens = text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    ids: [...new Set(tokens.filter((t) => ARCHE_ID.test(t)))],
    invalid: tokens.filter((t) => !ARCHE_ID.test(t)),
  };
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function AppRolesRolloutCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const rollout = useQuery({
    queryKey: queryKeys.adminArcheGovernance.appRolesRollout(),
    queryFn: () => api.adminGetAppRolesRollout(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (next: AppRolesRolloutDto) => api.adminSetAppRolesRollout(next),
    onSuccess: (result) => {
      qc.setQueryData(queryKeys.adminArcheGovernance.appRolesRollout(), result);
      toast({ title: `App roles: ${result.mode}` });
    },
    onError: (err) =>
      toast({
        title: 'Could not change app roles',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const [mode, setMode] = useState<AppRolesRolloutMode>('off');
  const [pilotText, setPilotText] = useState('');
  const [confirming, setConfirming] = useState<AppRolesRolloutDto | null>(null);
  const current = rollout.data;
  useEffect(() => {
    if (!current) return;
    setMode(current.mode);
    setPilotText(current.pilotArcheIds.join('\n'));
  }, [current]);

  const pilot = parsePilot(pilotText);
  const next: AppRolesRolloutDto = { mode, pilotArcheIds: pilot.ids };
  const unchanged =
    !!current &&
    current.mode === next.mode &&
    current.pilotArcheIds.join(',') === next.pilotArcheIds.join(',');
  const blocked =
    pilot.invalid.length > 0 ||
    pilot.ids.length > PILOT_MAX ||
    (mode === 'pilot' && pilot.ids.length === 0);

  function submit(): void {
    if (!current) return;
    // Admitting more asks first; admitting less is one click.
    if (admitsMore(current, next)) setConfirming(next);
    else save.mutate(next);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          App roles (ADR-034)
        </CardTitle>
        <CardDescription>
          Gates only an app’s FIRST roles publish. Turning it off never removes roles from an app
          that has them: their workspaces stay protected and the app can keep publishing changes to
          its roles. Off is the safe reading: the server treats a missing or unreadable setting as
          off.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {rollout.isError ? (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-destructive">
            <p>Could not load the app roles rollout: {errorMessage(rollout.error)}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void rollout.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : !current ? (
          <div className="h-5 w-48 animate-pulse rounded bg-muted" aria-label="Loading app roles" />
        ) : (
          <>
            <p>
              App roles are{' '}
              <span data-testid="app-roles-rollout-status" className="font-semibold">
                {MODES.find((m) => m.mode === current.mode)?.label}
              </span>
              {current.mode === 'pilot' && ` for ${current.pilotArcheIds.length} app(s)`}.
            </p>
            <div role="radiogroup" aria-label="App roles rollout" className="flex gap-2">
              {MODES.map((m) => (
                <Button
                  key={m.mode}
                  role="radio"
                  aria-checked={mode === m.mode}
                  size="sm"
                  variant={mode === m.mode ? 'default' : 'outline'}
                  disabled={save.isPending}
                  onClick={() => setMode(m.mode)}
                >
                  {m.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {MODES.find((m) => m.mode === mode)?.meaning}
            </p>
            {mode === 'pilot' && (
              <div className="space-y-1">
                <Label htmlFor="app-roles-pilot">Pilot apps (arche ids, one per line)</Label>
                <Textarea
                  id="app-roles-pilot"
                  value={pilotText}
                  onChange={(e) => setPilotText(e.target.value)}
                  rows={4}
                  placeholder="arc_…"
                  disabled={save.isPending}
                />
                {pilot.invalid.length > 0 && (
                  <p className="text-xs text-destructive">
                    Not an arche id: {pilot.invalid.slice(0, 3).join(', ')}
                  </p>
                )}
                {pilot.ids.length > PILOT_MAX && (
                  <p className="text-xs text-destructive">At most {PILOT_MAX} pilot apps.</p>
                )}
              </div>
            )}
            <Button size="sm" disabled={unchanged || blocked || save.isPending} onClick={submit}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Other servers pick a change up within about 30 seconds. Every change is audited.
            </p>
          </>
        )}
      </CardContent>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Let more apps publish roles?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.mode === 'on'
                ? 'Any app will be able to publish roles.'
                : `The ${confirming?.pilotArcheIds.length ?? 0} listed app(s) will be able to publish roles.`}{' '}
              An app that publishes roles keeps them: turning this off later stops new apps only, it
              never removes roles from one that has them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const value = confirming;
                setConfirming(null);
                if (value) save.mutate(value);
              }}
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
